import { makeStyles } from '@material-ui/styles'
import LearnMoreLink from 'core/components/LearnMoreLink'
import { FormFieldCard } from 'core/components/validatedForm/FormFieldCard'
import PlusButton from 'core/elements/button/PlusButton'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import { useCallback } from 'react'
import { trackEvent } from 'utils/tracking'
import HostNetworkConfiguration, { defaultInterfaceConfig } from './HostNetworkConfiguration'
import { hostNetworkConfigurationDoc } from 'app/docLinks'
import { uniq } from 'ramda'
import { customValidator } from 'core/utils/fieldValidators'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'grid',
    gap: theme.spacing(3),
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: theme.spacing(2),
  },
  cardBody: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  infoText: {
    display: 'grid',
    gap: theme.spacing(0.5),
  },
}))

const defaultNetworkConfigValues = {
  name: '',
  networkInterfaces: [defaultInterfaceConfig(true)],
}

const extractVirtualNetworkInterface = (allConfigs = []) => {
  if (!allConfigs || allConfigs.length === 0) {
    return []
  }

  const virtualNetworkLabels = []
  for (const config of allConfigs) {
    for (const iface of config.networkInterfaces) {
      if (iface.tunnel) {
        virtualNetworkLabels.push(iface.physicalNetworkLabel)
      }
    }
  }
  return virtualNetworkLabels
}

export default function HostNetworkConfigurations({
  existingHostNetworkConfigs,
  params,
  updateParams,
  hostConfigErrorIndexes = [],
  setHostConfigErrorIndexes,
}) {
  const classes = useStyles()

  const addConfiguration = useCallback(() => {
    updateParams({
      hostNetworkConfigs: [...params.hostNetworkConfigs, defaultNetworkConfigValues],
    })
    trackEvent(`Cluster Blueprint - Clicked Add Host Network Configuration`)
  }, [params.hostNetworkConfigs])

  const virtualNetworkLabelValidator = useCallback(
    (isExistingNetworkInterface: boolean) =>
      customValidator((value) => {
        // Do not show for existing network interfaces
        if (isExistingNetworkInterface) return true

        // Else check for unique labels across all configurations
        const virtualNetworkLabels = extractVirtualNetworkInterface(params.hostNetworkConfigs)
        const uniqueLabels = uniq(virtualNetworkLabels)
        return uniqueLabels.length === 1
      }, `The physical network label for the network interface serving virtual network tunnels must be the same across all Host Network Configurations`),
    [params.hostNetworkConfigs],
  )

  return (
    <FormFieldCard title="Host Configurations" fullWidth>
      <div className={classes.cardBody}>
        <div className={classes.infoText}>
          <Text variant="body2" component="p">
            Hosts in a cluster are automatically configured for you based on a Host Configuration.
          </Text>
          <Text variant="body2" component="p">
            List each network interface on your hosts, and assign it system traffic if any, and/or a
            physical network label. You can later create Physical Networks using physical network
            labels. <LearnMoreLink url={hostNetworkConfigurationDoc} />
          </Text>
        </div>
        <div className={classes.root}>
          {params?.hostNetworkConfigs?.map((config, idx) => (
            <div key={idx}>
              <HostNetworkConfiguration
                params={params}
                updateParams={updateParams}
                config={config}
                idx={idx}
                hasError={hostConfigErrorIndexes?.includes(idx)}
                existingHostNetworkConfig={existingHostNetworkConfigs?.find(
                  (c) => c.name === config.name,
                )}
                setHostConfigErrorIndexes={setHostConfigErrorIndexes}
                virtualNetworkLabelValidator={virtualNetworkLabelValidator}
              />
            </div>
          ))}
          <div className={classes.buttonRow}>
            <PlusButton
              onClick={addConfiguration}
              label="Add Host Configuration"
              data-testid="add-host-configuration-button"
            />
          </div>
        </div>
      </div>
    </FormFieldCard>
  )
}
